import { z } from 'zod';
import moment from 'moment';
import { DealActivityStatus, DealActivityEventType, DealActivityEventPriority } from '@trnsact/trnsact-shared-types';

import { CalendarMapper } from '../../mapper';
import { dateStringSchema } from '../../model';

const timeStringSchema = z.string().nullable();

export const createEventFormSchema = z
  .object({
    description: z.string(),
    allDay: z.boolean(),
    assignedTo: z.string(),
    startDate: dateStringSchema,
    endDate: dateStringSchema,
    startTime: timeStringSchema,
    endTime: timeStringSchema,
    eventType: z.nativeEnum(DealActivityEventType),
    priority: z.nativeEnum(DealActivityEventPriority),
    activityStatus: z.nativeEnum(DealActivityStatus),
    accountId: z.string().uuid({ message: 'Required' }),
    subject: z.string().min(1, 'Subject is required'),
    contactId: z.string().uuid().optional().nullable().or(z.literal('')),
  })
  .superRefine((data, ctx) => {
    if (!data.startDate) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Start date is required', path: ['startDate'] });
    }

    if (!data.endDate) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'End date is required', path: ['endDate'] });
    }

    if (data.startDate && data.endDate && !data.allDay) {
      if (!data.startTime) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Start time is required', path: ['startTime'] });
      }

      if (!data.endTime) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'End time is required', path: ['endTime'] });
      }
    }

    if (data.allDay && data.startDate && data.endDate && new Date(data.endDate) < new Date(data.startDate)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'End date must be after start date', path: ['endDate'] });
    }
  });

export type CreateEventFormValues = z.infer<typeof createEventFormSchema>;

export const getDefaultEventTimes = () => {
  const startTime = moment().add(1, 'hour').startOf('hour').format('HH:mm');
  const { time: endTime } = CalendarMapper.addOneHour(startTime);

  return { startTime, endTime };
};

export const getCreateEventFormDefaultValues = (userFullName: string, accountId = ''): CreateEventFormValues => {
  const now = moment();
  const { startTime, endTime } = getDefaultEventTimes();
  const { nextDay } = CalendarMapper.addOneHour(startTime);

  const startDate = now.toISOString();
  const endDate = nextDay ? now.clone().add(1, 'day').toISOString() : startDate;

  return {
    endTime,
    endDate,
    startTime,
    startDate,
    subject: '',
    allDay: false,
    accountId,
    contactId: '',
    description: '',
    assignedTo: userFullName,
    activityStatus: DealActivityStatus.Open,
    eventType: DealActivityEventType.PhoneCall,
    priority: DealActivityEventPriority.Medium,
  };
};
